import React from 'react';
import { useViewMode, VIEW_MODES } from '@/contexts/ViewModeContext';
import {
  Monitor,
  Question as HelpCircle,
  Users
} from '@phosphor-icons/react';

/**
 * Toggle para cambiar entre Vista Simple (PYME) y Vista Tecnica
 * Onyx Cyan calibrado
 */
export function ViewModeToggle({ className = '' }) {
  const { viewMode, toggleViewMode, isSimpleMode } = useViewMode();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={toggleViewMode}
        className={`relative flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
          isSimpleMode
            ? 'bg-emerald-500/[0.08] border-emerald-500/[0.3] text-emerald-400'
            : 'bg-cyan-500/[0.08] border-cyan-500/[0.3] text-cyan-400'
        } hover:bg-white/[0.06]`}
        title={isSimpleMode ? 'Cambiar a Vista Tecnica' : 'Cambiar a Vista Simple'}
        aria-label={isSimpleMode ? 'Cambiar a Vista Tecnica' : 'Cambiar a Vista Simple'}
      >
        {isSimpleMode ? (
          <>
            <Users className="h-3.5 w-3.5" />
            <span className="hidden sm:inline font-mono uppercase tracking-wider text-[11px]">
              Vista Simple
            </span>
          </>
        ) : (
          <>
            <Monitor className="h-3.5 w-3.5" />
            <span className="hidden sm:inline font-mono uppercase tracking-wider text-[11px]">
              Vista Tecnica
            </span>
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Componente que muestra contenido diferente segun el modo de vista
 */
export function ViewModeContent({ simple, technical }) {
  const { isSimpleMode } = useViewMode();
  return isSimpleMode ? simple : technical;
}

/**
 * Tooltip explicativo para terminos tecnicos — hairline Onyx
 */
export function TechTooltip({ term, explanation, children }) {
  const { isSimpleMode } = useViewMode();

  if (!isSimpleMode) {
    return children;
  }

  return (
    <span className="relative group cursor-help">
      {children}
      <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-md bg-slate-900/95 backdrop-blur-xl border border-white/[0.08] text-xs text-slate-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-xl">
        <span className="font-mono uppercase tracking-wider text-[10px] text-cyan-400">
          {term}
        </span>
        <br />
        <span className="text-slate-300">{explanation}</span>
      </span>
    </span>
  );
}

/**
 * Badge de severidad — Onyx Cyan calibrado, sin emojis ni neon
 */
export function SeverityBadge({ severity, count }) {
  const { isSimpleMode } = useViewMode();

  const config = {
    critical: {
      simple: 'Urgente',
      technical: 'CRITICAL',
      classes: 'bg-red-500/[0.08] border-red-500/[0.2] text-red-400',
    },
    high: {
      simple: 'Importante',
      technical: 'HIGH',
      classes: 'bg-orange-500/[0.08] border-orange-500/[0.2] text-orange-400',
    },
    medium: {
      simple: 'Moderado',
      technical: 'MEDIUM',
      classes: 'bg-yellow-500/[0.08] border-yellow-500/[0.2] text-yellow-400',
    },
    low: {
      simple: 'Menor',
      technical: 'LOW',
      classes: 'bg-emerald-500/[0.08] border-emerald-500/[0.2] text-emerald-400',
    },
  };

  const entry = config[severity?.toLowerCase()] || config.medium;
  const label = isSimpleMode ? entry.simple : entry.technical;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider border ${entry.classes}`}
    >
      {label}
      {count !== undefined && (
        <span className="tabular-nums font-semibold">{count}</span>
      )}
    </span>
  );
}

export default ViewModeToggle;
